import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, apiAdres, uitvoeren } from './client.ts';
import { kandidaatSleutels } from './kandidaten.ts';
import { useApiMutatie } from './mutaties.ts';
import type { CvVerwerkingsstatus, IndelingInvoer, Paginering } from './typen.ts';

export const cvSleutels = {
  alle: ['cvs'] as const,
  vanKandidaat: (kandidaatId: string) => ['cvs', 'kandidaat', kandidaatId] as const,
  wachtrij: (paginering: Paginering) => ['cvs', 'wachtrij', paginering] as const,
  details: (id: string) => ['cvs', id] as const,
};

/**
 * Somt de cv's van een kandidaat op, nieuwste eerst.
 * @param kandidaatId Het id van de kandidaat.
 * @returns De query met de cv's.
 */
export function useCvsVanKandidaat(kandidaatId: string | undefined) {
  return useQuery({
    queryKey: cvSleutels.vanKandidaat(kandidaatId ?? ''),
    queryFn: () =>
      uitvoeren(
        api.GET('/api/v1/kandidaten/{id}/cvs', { params: { path: { id: kandidaatId ?? '' }, query: { limiet: 100 } } }),
      ),
    enabled: Boolean(kandidaatId),
  });
}

/**
 * Somt de cv's op die wachten op verwerking, oudste eerst.
 * @param paginering Limiet en offset.
 * @returns De query met de cv's in de wachtrij.
 */
export function useCvWachtrij(paginering: Paginering) {
  return useQuery({
    queryKey: cvSleutels.wachtrij(paginering),
    queryFn: () => uitvoeren(api.GET('/api/v1/cvs/wachtrij', { params: { query: paginering } })),
    placeholderData: keepPreviousData,
  });
}

/**
 * Haalt een cv op met zijn indeling.
 * @param id Het id van het cv.
 * @returns De query met het cv.
 */
export function useCv(id: string | null | undefined) {
  return useQuery({
    queryKey: cvSleutels.details(id ?? ''),
    queryFn: () => uitvoeren(api.GET('/api/v1/cvs/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: Boolean(id),
  });
}

/**
 * Het adres om het bestand van een cv te downloaden. De browser stuurt de sessiecookie mee.
 * @param id Het id van het cv.
 * @returns Het adres.
 */
export function cvDownloadAdres(id: string): string {
  return apiAdres(`/api/v1/cvs/${encodeURIComponent(id)}/bestand`);
}

/**
 * De mutaties op cv's: uploaden, actueel maken, indelen, verwerkingsstatus zetten en verwijderen.
 * @returns De mutaties.
 */
export function useCvMutaties() {
  const verversen = ({ kandidaatId }: { kandidaatId: string }) => [
    cvSleutels.alle,
    cvSleutels.vanKandidaat(kandidaatId),
    kandidaatSleutels.profiel(kandidaatId),
  ];
  return {
    upload: useApiMutatie(
      ({ kandidaatId, bestand, actueel }: { kandidaatId: string; bestand: File; actueel: boolean }) =>
        uitvoeren(
          api.POST('/api/v1/kandidaten/{id}/cvs', {
            params: { path: { id: kandidaatId }, query: { actueel: actueel ? 'true' : 'false' } },
            body: { bestand: bestand as unknown as string },
            bodySerializer: () => {
              const formulier = new FormData();
              formulier.append('bestand', bestand, bestand.name);
              return formulier;
            },
          }),
        ),
      verversen,
    ),
    maakActueel: useApiMutatie(
      ({ id }: { id: string; kandidaatId: string }) =>
        uitvoeren(api.POST('/api/v1/cvs/{id}/actueel', { params: { path: { id } } })),
      verversen,
    ),
    zetIndeling: useApiMutatie(
      ({ id, invoer }: { id: string; kandidaatId: string; invoer: IndelingInvoer }) =>
        uitvoeren(api.PUT('/api/v1/cvs/{id}/indeling', { params: { path: { id } }, body: invoer })),
      verversen,
    ),
    zetVerwerkingsstatus: useApiMutatie(
      ({ id, status }: { id: string; kandidaatId: string; status: CvVerwerkingsstatus }) =>
        uitvoeren(api.PUT('/api/v1/cvs/{id}/verwerkingsstatus', { params: { path: { id } }, body: { status } })),
      verversen,
    ),
    verwijder: useApiMutatie(
      ({ id, ontkoppelKwalificaties }: { id: string; kandidaatId: string; ontkoppelKwalificaties: boolean }) =>
        uitvoeren(
          api.DELETE('/api/v1/cvs/{id}', {
            params: { path: { id }, query: { ontkoppelKwalificaties: ontkoppelKwalificaties ? 'true' : 'false' } },
          }),
        ),
      verversen,
    ),
  };
}
